'use client';

import { useMemo } from 'react';
import { motion } from 'framer-motion';

import type { SkyboxTheme } from '@/features/lobby/theming/skyboxes/types';
import type { SkyboxPreset } from '../data/presets';

/**
 * Per-theme atmospheric accent layered on top of the registry sky thumbnail.
 *
 * Previous iterations of this spec included:
 *   • a rotating `conic-gradient` (produced a radar-scan wrap seam — removed);
 *   • a 2px mid-frame `horizon` gradient strip (produced a visible horizontal
 *     seam bisecting the preview — removed).
 *
 * What remains is a single static **halo**: a radial (or stacked radial) glow
 * describing the theme's light source — daylight pocket, aurora curtain,
 * nebula cloud, ember, etc. Radial gradients have no start/end seam so the
 * preview reads as one continuous, still sky. Depth at the frame edges comes
 * from the consumer's own vertical wash (`LaunchPreview`, `ReviewStep`) plus
 * the star twinkle layer defined below.
 *
 * Kept as a function (not a const map) so theme-driven accents can thread
 * through to tokenised gradient stops — used by `farm` and `aurora` today.
 * `accent` is unused by themes whose palettes are fully baked into the
 * gradient stops; the parameter stays in the signature so future themes can
 * opt into accent-aware glows without touching callers.
 */
export function liveSkyBackdropSpec(
  id: SkyboxTheme,
  accent: string,
): { halo: string } {
  switch (id) {
    case 'sky':
      return {
        halo:
          'radial-gradient(ellipse 60% 42% at 72% 22%, rgba(255,255,255,0.38) 0%, rgba(255,255,255,0.08) 38%, transparent 62%)',
      };
    case 'aurora':
      return {
        halo:
          `radial-gradient(ellipse 55% 38% at 30% 28%, rgba(0,255,140,0.3) 0%, rgba(0,255,140,0.08) 40%, transparent 62%), radial-gradient(ellipse 50% 32% at 72% 40%, rgba(160,60,255,0.26) 0%, rgba(160,60,255,0.06) 42%, transparent 64%)`,
      };
    case 'sci-fi':
      return {
        halo:
          'radial-gradient(ellipse 65% 45% at 60% 32%, rgba(255,110,200,0.32) 0%, rgba(255,110,200,0.08) 45%, transparent 65%), radial-gradient(ellipse 50% 35% at 28% 58%, rgba(100,200,255,0.22) 0%, transparent 58%)',
      };
    case 'medieval':
      return {
        halo:
          'radial-gradient(ellipse 55% 30% at 50% 55%, rgba(255,140,70,0.3) 0%, rgba(255,140,70,0.08) 45%, transparent 65%)',
      };
    case 'desert':
      return {
        halo:
          'radial-gradient(ellipse 55% 38% at 78% 20%, rgba(255,255,255,0.45) 0%, rgba(255,255,255,0.1) 40%, transparent 62%)',
      };
    case 'sanctum':
      // Crimson sigil at the apex + ember band at the floor. Kept as a
      // defensive case — sanctum is filtered out of the launch wizard's
      // `PUBLIC_SKYBOX_PRESETS`, but if anything ever does reach the
      // 2D preview (dev tooling, future admin surfaces) the halo
      // matches what the 3D shader actually paints.
      return {
        halo:
          'radial-gradient(ellipse 30% 22% at 50% 18%, rgba(216,48,72,0.55) 0%, rgba(140,32,80,0.18) 38%, transparent 62%), radial-gradient(ellipse 70% 26% at 50% 92%, rgba(180,20,30,0.42) 0%, transparent 60%)',
      };
    case 'farm':
    default:
      return {
        halo: `radial-gradient(ellipse 60% 40% at 50% 45%, ${accent}55 0%, ${accent}15 40%, transparent 62%)`,
      };
  }
}

type Variant = 'tile' | 'hero';

/**
 * Atmospheric overlay: static halo + horizon line + twinkling stars, painted
 * on top of the parent's `background` (the registry thumbnail, which is
 * already a clean vertical land→sky gradient). Kept deliberately still —
 * earlier iterations spun a conic gradient for "drift" but that produced a
 * visible radar-scan seam on the large preview. Motion is supplied entirely
 * by the star twinkle, which is seamless because each star animates its own
 * opacity/scale in-place.
 */
export function SkyAtmosphereLayers({
  sky,
  variant = 'tile',
}: {
  sky: SkyboxPreset;
  variant?: Variant;
}) {
  const stars = useMemo(() => {
    const seed = sky.id.split('').reduce((n, c) => n + c.charCodeAt(0), 0);
    const count = variant === 'hero' ? 18 : 10;
    return Array.from({ length: count }, (_, i) => {
      const t = Math.sin(seed * 13.37 + i * 7.7);
      // Keep stars in the upper ~48% so themes whose thumbnails have a
      // visible ground band (Desert, Dusk, Homestead) don't get stars in
      // the dirt. 4% top margin prevents clipping at the frame edge.
      return {
        left: 6 + ((t + 1) * 88) % 88,
        top: 4 + ((Math.cos(seed * 9.1 + i * 3.3) + 1) * 44) % 44,
        size: (variant === 'hero' ? 1.8 : 1.5) + (i % 3) * 0.8,
        delay: (i * 0.22) % 2.4,
        duration: 1.5 + (i % 4) * 0.5,
      };
    });
  }, [sky.id, variant]);

  const bd = liveSkyBackdropSpec(sky.id, sky.accent);

  return (
    <>
      {/* Static atmospheric halo. Radial gradients have no start/end seam,
          so there's nothing to "sweep" across the frame — the preview reads
          as one continuous, still sky with a light pocket wherever the
          theme's key light sits. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background: bd.halo,
          mixBlendMode: variant === 'hero' ? 'screen' : 'screen',
          opacity: variant === 'hero' ? 0.85 : 1,
        }}
      />

      {variant === 'hero' && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/[0.04] via-transparent to-black/25"
        />
      )}

      {/* A 2px mid-frame "horizon" strip used to live here. It produced a
          visible horizontal seam across the preview — exactly the kind of
          cut the user wanted gone — so it's been removed. Depth now comes
          from the consumer's own vertical wash + the star twinkle below,
          which leaves the base skybox thumbnail reading cleanly. */}

      {stars.map((star, i) => (
        <motion.span
          key={i}
          aria-hidden
          className="pointer-events-none absolute rounded-full"
          style={{
            left: `${star.left}%`,
            top: `${star.top}%`,
            width: star.size,
            height: star.size,
            background: sky.accent,
            boxShadow: `0 0 ${star.size * 2.5}px ${sky.accent}`,
            opacity: variant === 'hero' ? 0.9 : 1,
          }}
          animate={{ opacity: [0.2, 1, 0.2], scale: [1, 1.35, 1] }}
          transition={{
            duration: star.duration,
            delay: star.delay,
            ease: 'easeInOut',
            repeat: Infinity,
          }}
        />
      ))}
    </>
  );
}
